import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { monitoringAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export function MonitoringTokensPage() {
  const [days, setDays] = useState(30);
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser);

  const { data: overviewData } = useQuery({
    queryKey: ['monitoring', 'overview'],
    queryFn: () => monitoringAPI.overview(),
  });

  const { data: dailyData, isLoading } = useQuery({
    queryKey: ['monitoring', 'daily-usage', days],
    queryFn: () => monitoringAPI.dailyUsage(days),
  });

  const overview = overviewData?.data;
  const daily = dailyData?.data || [];

  // 누적 토큰 계산
  let cumulative = 0;
  const dailyWithCost = daily.map((d: any) => {
    const totalTokens = d.prompt_tokens + d.completion_tokens;
    const cost = (d.prompt_tokens * 0.15 + d.completion_tokens * 0.60) / 1_000_000;
    cumulative += totalTokens;
    return { ...d, total_tokens: totalTokens, cost: +cost.toFixed(4), cumulative_tokens: cumulative };
  });

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">토큰 / 비용</h1>
        <p className="text-gray-600">토큰 사용량과 비용 추이를 확인합니다.</p>
      </div>

      {/* 요약 카드 */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuperuser ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-8`}>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Prompt 토큰</p>
          <p className="text-2xl font-bold text-gray-900">{(overview?.total_prompt_tokens || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Completion 토큰</p>
          <p className="text-2xl font-bold text-gray-900">{(overview?.total_completion_tokens || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">총 토큰</p>
          <p className="text-2xl font-bold text-gray-900">{(overview?.total_tokens || 0).toLocaleString()}</p>
        </div>
        {/* [2026-03-24] 예상 비용은 슈퍼관리자만 */}
        {isSuperuser && (
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">예상 비용 (USD)</p>
            <p className="text-2xl font-bold text-orange-600">${overview?.estimated_cost_usd || '0.00'}</p>
            <p className="text-xs text-gray-400 mt-1">gpt-4o-mini 기준</p>
          </div>
        )}
      </div>

      {/* 기간 선택 */}
      <div className="flex gap-2 mb-6">
        {[7, 14, 30, 60].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              days === d ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
            }`}
          >
            {d}일
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">로딩 중...</div>
      ) : (
        <>
          {/* 일별 토큰 사용량 */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">일별 토큰 사용량</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyWithCost}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="prompt_tokens" name="Prompt" fill="#3B82F6" stackId="tokens" />
                  <Bar dataKey="completion_tokens" name="Completion" fill="#10B981" stackId="tokens" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 누적 토큰 추이 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">누적 토큰 추이</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyWithCost}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="cumulative_tokens" name="누적 토큰" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
